import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authForgotPassword, authResetPasswordPublic, setAdminToken, authVerify2fa } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";

/**
 * Two-step self-service password reset:
 *   1) Enter email + pick channel (email link  OR  SMS 6-digit code).
 *   2) Confirmation screen tells user what to do next. Email flow bounces
 *      to /admin/reset-password?token=xxx via SendGrid. SMS flow drops
 *      them straight onto a new-password form on the same page.
 */
export default function AdminForgotPasswordPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("start"); // 'start' | 'sent' | 'sms-code' | 'done'
  const [email, setEmail] = useState("");
  const [channel, setChannel] = useState("email"); // 'email' | 'sms'
  const [smsCode, setSmsCode] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const start = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await authForgotPassword(email.trim(), channel);
      setInfo(r.message || "If that email is on file, you'll receive a reset code shortly.");
      if (channel === "sms") {
        setStage("sms-code");
      } else {
        setStage("sent");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start the reset. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitSmsReset = async (e) => {
    e?.preventDefault();
    if (newPw.length < 8) return setError("Password must be at least 8 characters.");
    if (newPw !== confirmPw) return setError("Passwords don't match.");
    setBusy(true); setError(null);
    try {
      await authResetPasswordPublic(email.trim(), smsCode.trim(), newPw, "sms");
      setStage("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not reset. The code may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageWrap} data-testid="admin-forgot-page">
      <div style={card}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>NEARNOW · ADMIN PASSWORD RESET</div>
        <h1 style={h1}>
          {stage === "start" && "RESET YOUR PASSWORD"}
          {stage === "sent" && "CHECK YOUR EMAIL"}
          {stage === "sms-code" && "ENTER YOUR CODE"}
          {stage === "done" && "PASSWORD UPDATED"}
        </h1>

        {stage === "start" && (
          <form onSubmit={start}>
            <p style={{ color: "#5b5e69", marginBottom: 18 }}>
              Enter the email tied to your NearNow admin account. We&apos;ll send you a reset link by email — or, if you prefer, a 6-digit code to your 2FA phone.
            </p>
            <Label>ADMIN EMAIL</Label>
            <input type="email" autoFocus required value={email}
                   onChange={(e) => setEmail(e.target.value)}
                   data-testid="forgot-email-input" style={inputStyle}
                   placeholder="you@yourcompany.com" />

            <Label>WHERE SHOULD WE SEND THE CODE?</Label>
            <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
              <ChannelPill value="email" active={channel === "email"} onClick={() => setChannel("email")} testid="forgot-channel-email">
                Email link (30 min)
              </ChannelPill>
              <ChannelPill value="sms" active={channel === "sms"} onClick={() => setChannel("sms")} testid="forgot-channel-sms">
                SMS code (10 min)
              </ChannelPill>
            </div>

            {error && <p data-testid="forgot-error" style={errBox}>{error}</p>}
            <button type="submit" disabled={busy || !email}
                    data-testid="forgot-submit-button"
                    style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
              {busy ? "Sending…" : `SEND RESET ${channel === "sms" ? "CODE" : "LINK"} →`}
            </button>
            <button type="button" onClick={() => navigate("/admin/login")} style={btnGhost} data-testid="forgot-back-link">
              ← Back to sign-in
            </button>
          </form>
        )}

        {stage === "sent" && (
          <>
            <p style={{ color: "#5b5e69" }} data-testid="forgot-sent-info">
              {info} Check your inbox (and spam folder). The reset link is valid for 30 minutes.
            </p>
            <button onClick={() => navigate("/admin/login")} style={btnPrimary} data-testid="forgot-back-button">
              ← BACK TO SIGN-IN
            </button>
          </>
        )}

        {stage === "sms-code" && (
          <form onSubmit={submitSmsReset}>
            <p style={{ color: "#5b5e69", marginBottom: 18 }} data-testid="forgot-sms-info">
              {info} Enter the 6-digit code below along with your new password.
            </p>
            <Label>SMS CODE</Label>
            <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                   required value={smsCode}
                   onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ""))}
                   data-testid="forgot-sms-code-input"
                   style={{ ...inputStyle, letterSpacing: 14, fontSize: 24, textAlign: "center", fontWeight: 700 }}
                   placeholder="000000" />
            <Label>NEW PASSWORD (min 8 chars)</Label>
            <input type="password" required minLength={8} value={newPw}
                   onChange={(e) => setNewPw(e.target.value)}
                   data-testid="forgot-new-password-input" style={inputStyle} />
            <Label>CONFIRM NEW PASSWORD</Label>
            <input type="password" required minLength={8} value={confirmPw}
                   onChange={(e) => setConfirmPw(e.target.value)}
                   data-testid="forgot-confirm-password-input" style={inputStyle} />
            {error && <p data-testid="forgot-error" style={errBox}>{error}</p>}
            <button type="submit" disabled={busy || smsCode.length !== 6}
                    data-testid="forgot-sms-submit-button"
                    style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
              {busy ? "Resetting…" : "RESET MY PASSWORD →"}
            </button>
          </form>
        )}

        {stage === "done" && (
          <>
            <p style={{ color: "#1A7A4A", fontWeight: 700 }} data-testid="forgot-done-info">
              ✓ Your password has been updated. You can now sign in with your new password.
            </p>
            <button onClick={() => navigate("/admin/login")} style={btnPrimary} data-testid="forgot-signin-button">
              GO TO SIGN-IN →
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const ChannelPill = ({ children, active, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
          style={{ flex: 1, padding: "12px 14px",
                   background: active ? NAVY : "#fff",
                   color: active ? GOLD : NAVY,
                   border: `1px solid ${active ? NAVY : "#d0d3da"}`,
                   borderRadius: 10, fontSize: 13, fontWeight: 700, letterSpacing: 1, cursor: "pointer" }}>
    {children}
  </button>
);

const Label = ({ children }) => (
  <div style={{ color: NAVY, letterSpacing: 2, fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{children}</div>
);

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 };
const card = { background: "#fff", borderRadius: 18, padding: "40px 36px", maxWidth: 460, width: "100%", border: `2px solid ${GOLD}`, boxShadow: "0 30px 60px -25px rgba(10,22,40,0.25)" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 34, color: NAVY, margin: "4px 0 22px" };
const inputStyle = { width: "100%", padding: "14px 16px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 16, marginBottom: 18, outline: "none", boxSizing: "border-box" };
const btnPrimary = { width: "100%", background: GOLD, color: NAVY, border: "none", padding: "16px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 16, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 4 };
const btnGhost = { width: "100%", background: "transparent", color: NAVY, border: "none", padding: "10px", marginTop: 8, cursor: "pointer", fontSize: 13 };
const errBox = { color: "#A32D2D", background: "#FDECEC", padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
